import React, { createContext, useContext, useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { User, Session } from "@supabase/supabase-js";
import { toast } from "sonner";
import { secureLog } from "@/utils/securityUtils";

type AuthContextType = {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isEmailConfirmed: boolean;
  signUp: (email: string, password: string, userData: UserSignUpData) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
};

export type UserSignUpData = {
  username: string;
  firstName: string;
  lastName: string;
  birthdate: string;
  referralCode?: string;
  marketingConsent?: boolean;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  secureLog.info('AuthProvider render - React available:', !!React);
  secureLog.info('AuthProvider render - useState available:', !!useState);
  
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isEmailConfirmed, setIsEmailConfirmed] = useState(false);

  const validateUserProfile = async (userId: string): Promise<boolean> => {
    try {
      // First check if profile exists
      const { data, error } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', userId)
        .maybeSingle();
      
      if (error) {
        secureLog.error('Error checking profile existence:', error);
        return false;
      }
      
      // If profile exists, return true
      if (data) {
        return true;
      }
      
      // Profile doesn't exist - try to create it
      secureLog.warn('Profile not found for user, attempting to create:', userId);
      
      try {
        const { data: createResult, error: createError } = await supabase
          .rpc('create_missing_profile_for_user', {
            user_id_param: userId
          });
        
        if (createError) {
          secureLog.error('Error creating missing profile:', createError);
          return false;
        }
        
        // Type assertion for the RPC result
        const result = createResult as { success: boolean; created?: boolean; error?: string; message?: string };
        
        if (result?.success) {
          secureLog.info('Successfully created missing profile for user:', userId);
          if (result.created) {
            toast.success('Ihr Profil wurde erfolgreich wiederhergestellt!');
          }
          return true;
        } else {
          secureLog.error('Failed to create missing profile:', result?.error || 'Unknown error');
          return false;
        }
      } catch (createError) {
        secureLog.error('Exception while creating missing profile:', createError);
        return false;
      }
    } catch (error) {
      secureLog.error('Profile validation failed:', error);
      return false;
    }
  };

  useEffect(() => {
    // Set up auth state listener first
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        secureLog.info('Auth state change:', event, session?.user?.id);
        secureLog.info('User email confirmed:', session?.user?.email_confirmed_at);
        setSession(session);
        setUser(session?.user ?? null);
        setIsEmailConfirmed(!!session?.user?.email_confirmed_at);
        
        if (event === 'SIGNED_IN') {
          // Validate profile existence before confirming login
          if (session?.user) {
            setTimeout(async () => {
              const hasProfile = await validateUserProfile(session.user.id);
              if (!hasProfile) {
                secureLog.warn('User logged in without profile, signing out:', session.user.id);
                toast.error("Ihr Konto wurde nicht korrekt eingerichtet. Bitte registrieren Sie sich erneut oder kontaktieren Sie den Support.");
                await supabase.auth.signOut();
                return;
              }
              
              // Process pending profile invitations for this user
              if (session.user.email) {
                try {
                  const { data: processedInvitations, error } = await supabase.rpc('process_pending_invitations_for_user', {
                    user_email: session.user.email
                  });
                  
                  if (!error && processedInvitations && processedInvitations.length > 0) {
                    const count = processedInvitations.length;
                    secureLog.info(`Processed ${count} pending invitation(s) for user:`, session.user.id);
                  }
                } catch (error) {
                  secureLog.error('Error processing pending invitations:', error);
                }
              }
              
              // Only show toast for actual user logins, not token refreshes
              if (isLoggingIn) {
                toast.success("Erfolgreich angemeldet!");
                setIsLoggingIn(false);
              }
              
              // Handle referral session processing for new users
              if (session.user.user_metadata?.referralCode) {
                secureLog.info('Processing referral code from user metadata:', session.user.user_metadata.referralCode);
                await processReferralSession(session.user.id, session.user.user_metadata.referralCode);
              }
            }, 0);
          }
        } else if (event === 'SIGNED_OUT') {
          toast.info("Abgemeldet");
        } else if (event === 'USER_UPDATED') {
          secureLog.info('User updated event received');
        }
      }
    );

    // Then check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      secureLog.info('Initial session check:', session?.user?.id);
      setSession(session);
      setUser(session?.user ?? null);
      setIsEmailConfirmed(!!session?.user?.email_confirmed_at);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const processReferralSession = async (newUserId: string, sessionToken: string) => {
    try {
      console.log('🎯 Processing referral session for user:', newUserId, 'token:', sessionToken?.substring(0, 8) + '...');
      secureLog.info('Processing referral session for user:', newUserId, 'token:', sessionToken);
      
      // First check if this is a valid session token
      const { data: sessionData, error: sessionError } = await supabase
        .from('referral_sessions')
        .select(`
          id,
          referrer_profile_id,
          invited_email,
          is_used,
          expires_at
        `)
        .eq('session_token', sessionToken)
        .single();

      console.log('📋 Session lookup result:', { sessionData, sessionError });
      
      if (sessionError || !sessionData) {
        console.log('⚠️ No valid session found for token');
        secureLog.warn('No valid referral session found for token');
        return;
      }

      if (sessionData.is_used) {
        console.log('⚠️ Session already used');
        secureLog.warn('Referral session already used');
        return;
      }

      if (new Date(sessionData.expires_at) < new Date()) {
        console.log('⚠️ Session expired');
        secureLog.warn('Referral session expired');
        return;
      }

      console.log('✅ Valid session found, referrer ID:', sessionData.referrer_profile_id);
      
      // Call the database function to process the referral session
      const { data: result, error } = await supabase
        .rpc('process_referral_session', {
          session_token_param: sessionToken,
          user_id_param: newUserId
        });

      console.log('🔄 Referral processing result:', { result, error });

      if (error) {
        console.error('❌ Error processing referral session:', error);
        secureLog.error('Error processing referral session:', error);
        return;
      }

      const typedResult = result as { success?: boolean; error?: string; referrer_profile_id?: string };
      if (typedResult?.success) {
        console.log('🎉 Referral session processed successfully!');
        console.log('👤 Referred by profile ID:', typedResult.referrer_profile_id);
        secureLog.info('Referral session processed successfully:', result);
        toast.success("Willkommen! Du wurdest erfolgreich über einen Einladungslink registriert.");
        
        // Log the successful referral for debugging
        setTimeout(async () => {
          const { data: newReferral } = await supabase
            .from('referrals')
            .select('*')
            .eq('referred_id', newUserId)
            .single();
          console.log('📝 New referral record created:', newReferral);
        }, 1000);
      } else {
        console.log('❌ Referral session processing failed:', typedResult?.error);
        secureLog.warn('Referral session processing failed:', typedResult?.error);
      }
    } catch (error) {
      console.error('💥 Exception in referral session processing:', error);
      secureLog.error('Error in referral session processing:', error);
    }
  };

  const signUp = async (email: string, password: string, userData: UserSignUpData) => {
    try {
      setLoading(true);
      secureLog.info('Starting signup process for:', email);
      secureLog.info('User data:', userData);
      
      // Validate required fields
      if (!userData.username.trim()) {
        throw new Error('Benutzername ist erforderlich');
      }
      
      if (!userData.firstName.trim()) {
        throw new Error('Vorname ist erforderlich');
      }
      
      if (!userData.lastName.trim()) {
        throw new Error('Nachname ist erforderlich');
      }
      

      // Build redirect URL with referral token in user metadata for email confirmation
      const baseUrl = 'https://app.uplivion.life';
      const redirectUrl = `${baseUrl}/dashboard`;
      secureLog.info('Using redirect URL for email confirmation:', redirectUrl);

      const { data, error } = await supabase.auth.signUp({ 
        email, 
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            username: userData.username.trim(),
            firstName: userData.firstName.trim(),
            lastName: userData.lastName.trim(),
            birthdate: userData.birthdate,
            referralCode: userData.referralCode || null,
            marketingConsent: userData.marketingConsent || false
          }
        }
      });
      
      if (error) {
        secureLog.error('Signup error:', error);
        
        // Handle specific error cases with better messaging
        if (error.message.includes('User already registered')) {
          secureLog.warn('User already exists, this should trigger a repeated signup email');
          throw new Error('Ein Benutzer mit dieser E-Mail-Adresse existiert bereits');
        } else if (error.message.includes('Password should be at least')) {
          throw new Error('Das Passwort muss mindestens 8 Zeichen lang sein und die Sicherheitsanforderungen erfüllen');
        } else if (error.message.includes('Invalid email')) {
          throw new Error('Bitte gib eine gültige E-Mail-Adresse ein');
        } else if (error.message.includes('Password is too weak') || error.message.includes('leaked')) {
          throw new Error('Dieses Passwort wurde in Datenlecks gefunden oder ist zu schwach. Bitte wähle aus Sicherheitsgründen ein stärkeres Passwort.');
        } else if (error.message.includes('Password does not meet requirements')) {
          throw new Error('Das Passwort erfüllt nicht die Sicherheitsanforderungen. Bitte verwende mindestens 8 Zeichen mit Groß- und Kleinbuchstaben, Zahlen und Sonderzeichen.');
        } else {
          throw error;
        }
      }
      
      secureLog.info('Signup successful:', data.user?.id);
      secureLog.info('User needs email confirmation:', !!data.user && !data.session);
      secureLog.info('User email confirmed at:', data.user?.email_confirmed_at);
      
      // Only show success toast, don't navigate - let the form handle showing confirmation screen
      toast.success("Registrierung erfolgreich! Bitte bestätige deine E-Mail.");
      
    } catch (error: any) {
      secureLog.error('Signup process error:', error);
      toast.error(`Fehler bei der Registrierung: ${error.message}`);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true);
      setIsLoggingIn(true);
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      
      if (error) {
        // Translate common auth errors to German
        let errorMessage = error.message;
        if (error.message === 'Invalid login credentials') {
          errorMessage = 'Ungültige Anmeldedaten';
        } else if (error.message === 'Email not confirmed') {
          errorMessage = 'E-Mail nicht bestätigt';
        } else if (error.message === 'Too many requests') {
          errorMessage = 'Zu viele Anfragen';
        }
        toast.error(`Fehler bei der Anmeldung: ${errorMessage}`);
        throw error;
      }
      
      // Remove navigation from here - let components handle their own navigation
    } catch (error: any) {
      setIsLoggingIn(false);
      // This error is already handled above with translation
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        toast.error(error.message);
        throw error;
      }
      
      // Remove navigation from here - let components handle their own navigation
    } catch (error: any) {
      toast.error(`Fehler beim Abmelden: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, isEmailConfirmed, signUp, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
